import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StyledDataGrid from '../../shared/DataGrid/StyledDataGrid';
import dayjs from '../../shared/utils/dayJsRelativeTime';
import { DataGridContainer } from '../IngredientsPage';
import { TopBar } from '../ProductsPage';
import { ErrorGroup, usePaymentExceptions } from './hooks/usePaymentExceptions';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StyledLink } from '../../shared/StyledLink/StyledLink';
import { slugify } from '../../../../types/utils';
import { useAllBackofficeRestaurants } from '../../apis/shops-api';
import { notNull, notUndefined } from '../../shared/utils/typeUtils';

type IssueRestaurant = {
  restaurantName: string;
  shopExternalId: string;
  updatedAt: Date;
};

type Issue = {
  type: string;
  errorText: string;
  restaurants: IssueRestaurant[];
};

type PaymentExceptions =
  | {
      restaurantName: string;
      updatedAt: string;
      shopExternalId: string;
      errors: ErrorGroup;
    }[]
  | null;

const PaymentMethodsExceptionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { areaName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const singleIssueParams = searchParams.get('issue') || '';
  const { data: backofficeRestaurants, isLoading: isBackofficeRestaurantsLoading } = useAllBackofficeRestaurants();
  const { paymentExceptions } = usePaymentExceptions();

  const regions = useMemo((): string[] => {
    if (!backofficeRestaurants || isBackofficeRestaurantsLoading) return [];

    const regions = new Set(
      backofficeRestaurants
        .map((restaurant) => restaurant.regionName)
        .filter(notNull)
        .filter(notUndefined)
    );
    return [...regions];
  }, [backofficeRestaurants, isBackofficeRestaurantsLoading]);

  const selectedRegion = useMemo((): string | undefined => {
    return regions.find((region) => slugify(region) === slugify(areaName ?? ''));
  }, [regions, areaName]);

  const selectedRegionRestaurantIds = useMemo(() => {
    return backofficeRestaurants
      ? backofficeRestaurants
          .filter((restaurant) => restaurant.regionName === selectedRegion)
          .map((restaurant) => restaurant.shopExternalId)
      : [];
  }, [selectedRegion, backofficeRestaurants]);

  const issuesList = formatPaymentExceptionsToListOfIssues(paymentExceptions);
  const areaFilteredListforGrid = filterIssuesListByArea(issuesList, selectedRegion, selectedRegionRestaurantIds);
  const filteredListforGrid = filterIssuesListByParameters(areaFilteredListforGrid, singleIssueParams);
  const amountOfRestaurantsWithIssues = singleIssueParams
    ? filteredListforGrid.length
    : calculateUniqueRestaurantsWithIssues(areaFilteredListforGrid);

  const errorListColumns: GridColDef[] = [
    {
      field: 'type',
      headerName: t('Payment method'),
      flex: 1,
      renderCell: (params) => {
        return t(params.value);
      },
    },
    {
      field: 'restaurants',
      headerName: t('Restaurants'),
      flex: 2,
      renderCell: (params) => {
        return (
          <>
            {params.value ? params.value.length : ''}{' '}
            {params.value.length === 1 ? ` ${t('Restaurant')}` : ` ${t('Restaurants')}`}
          </>
        );
      },
    },
    {
      field: 'errorText',
      headerName: t('Issue'),
      flex: 8,
      renderCell: (params) => {
        const handleClick = () => {
          searchParams.set('issue', params.value);
          setSearchParams(searchParams);
        };
        return <StyledLink onClick={handleClick}>{params.value}</StyledLink>;
      },
    },
  ];

  const errorColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('ID'),
      flex: 0.5,
      renderCell: (params) => {
        const handleClick = () => {
          navigate('/restaurants/' + params.value);
        };
        return <StyledLink onClick={handleClick}>{params.value}</StyledLink>;
      },
    },
    {
      field: 'name',
      headerName: t('Restaurant'),
      flex: 3,
      renderCell: (params) => {
        return t(params.value);
      },
    },
    {
      field: 'updatedAt',
      headerName: t('Updated'),
      flex: 2,
      renderCell: (params) => {
        return t(dayjs(params.value).format('dd DD.MM.YYYY HH:mm'));
      },
    },
  ];

  const handleNavigation = (selectedRegion?: string) => {
    if (selectedRegion) {
      navigate(`/payment-methods-exceptions/area/${slugify(selectedRegion)}`);
    } else {
      navigate('/payment-methods-exceptions');
    }
  };

  if (!paymentExceptions) {
    return null;
  }

  return (
    <>
      <TopBar>
        <Typography variant="h1">
          {selectedRegion || singleIssueParams ? (
            <StyledLink onClick={() => handleNavigation()}> {t('Exceptions')}</StyledLink>
          ) : (
            t('Exceptions')
          )}
          {selectedRegion &&
            (singleIssueParams ? (
              <>
                /<StyledLink onClick={() => handleNavigation(selectedRegion)}>{selectedRegion}</StyledLink>
              </>
            ) : (
              <> /{selectedRegion}</>
            ))}
          {singleIssueParams && '/' + singleIssueParams}
        </Typography>
        <Row>
          <StyledFormControl>
            <InputLabel id="select-area-label">{t('Area')}</InputLabel>
            <Select
              labelId="select-area-label"
              id="select-area"
              value={selectedRegion || 'NoFilter'}
              label={t('Area')}
              onChange={(e) => {
                const selectedRegion = regions.find((region) => region === e.target.value);
                if (selectedRegion) {
                  if (singleIssueParams) {
                    navigate(
                      `/payment-methods-exceptions/area/${slugify(selectedRegion) + '?issue=' + singleIssueParams}`
                    );
                  } else {
                    navigate(`/payment-methods-exceptions/area/${slugify(selectedRegion)}`);
                  }
                } else {
                  if (singleIssueParams) {
                    navigate('/payment-methods-exceptions?issue=' + singleIssueParams);
                  } else {
                    navigate('/payment-methods-exceptions');
                  }
                }
              }}
            >
              <MenuItem key="NoFilter" value="NoFilter">
                {t('All restaurants')}
              </MenuItem>
              {regions.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Row>
      </TopBar>
      <RestaurantCounts>
        {amountOfRestaurantsWithIssues > 0 ? (
          <Typography variant="h2">
            ❌ {amountOfRestaurantsWithIssues} {t('restaurants has exceptions in payment methods')}
          </Typography>
        ) : (
          <Typography variant="h2">✅ {t('All selected restaurants are ok')}</Typography>
        )}
      </RestaurantCounts>
      <Typography variant="h2">
        <DataGridContainer display="flex">
          <StyledDataGrid
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            rows={filteredListforGrid || []}
            columns={singleIssueParams ? errorColumns : errorListColumns}
            getRowId={(row) => row.id || row.type + row.errorText}
            loading={isBackofficeRestaurantsLoading}
            initialState={{
              sorting: {
                sortModel: [{ field: 'errorText', sort: 'asc' }],
              },
            }}
          />
        </DataGridContainer>
      </Typography>
    </>
  );
};

const formatPaymentExceptionsToListOfIssues = (paymentExceptions: PaymentExceptions): Issue[] => {
  const map = new Map();

  if (paymentExceptions) {
    for (const item of paymentExceptions) {
      const { errors, restaurantName, shopExternalId, updatedAt } = item;
      if (!errors) continue;

      for (const [type, errorList] of Object.entries(errors)) {
        if (errorList.length === 0) continue;

        for (const errorText of errorList) {
          if (typeof errorText !== 'string') continue;

          //Remove shop specific info like: Suspended (2015002) so the are all grouped up
          const normalizedError = errorText.replace(/\s*\([^)]*\)\s*$/, '').trim();

          const key = `${type}||${normalizedError}`;

          if (!map.has(key)) {
            map.set(key, {
              type,
              errorText: normalizedError,
              restaurants: [{ shopExternalId, restaurantName, updatedAt }],
            });
          } else {
            map.get(key).restaurants.push({ shopExternalId, restaurantName, updatedAt });
          }
        }
      }
    }
  }

  return Array.from(map.values());
};

const filterIssuesListByArea = (
  issuesList: Issue[],
  selectedRegion?: string,
  selectedRegionRestaurantIds?: string[]
) => {
  const validIssues = issuesList ?? [];

  const issuesListByArea =
    selectedRegion && selectedRegionRestaurantIds
      ? validIssues
          .map((issue) => {
            const validRestaurants = issue.restaurants.filter((r) =>
              selectedRegionRestaurantIds.includes(r.shopExternalId)
            );
            return validRestaurants.length > 0 ? { ...issue, restaurants: validRestaurants } : null;
          })
          .filter((i): i is Issue => i !== null)
      : validIssues;

  return issuesListByArea;
};

const filterIssuesListByParameters = (validIssues: Issue[], singleIssueParams: string) => {
  const normalized = slugify(singleIssueParams);
  const singleIssue = validIssues.find((issue) => slugify(issue.errorText) === normalized);

  const restaurantsWithIssue =
    singleIssue?.restaurants.map((r) => ({
      id: r.shopExternalId,
      name: r.restaurantName,
      updatedAt: r.updatedAt,
    })) ?? [];

  return restaurantsWithIssue.length > 0 ? restaurantsWithIssue : validIssues;
};

const calculateUniqueRestaurantsWithIssues = (filteredListforGrid: Issue[]) => {
  const allRestaurants = filteredListforGrid.flatMap((issue) => issue.restaurants);

  const uniqueRestaurantsCount = [
    ...new Map(allRestaurants.map((r: IssueRestaurant) => [r.shopExternalId, r])).values(),
  ];

  return uniqueRestaurantsCount.length;
};

const RestaurantCounts = styled.div`
  padding-bottom: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  flex: 1;
`;

const StyledFormControl = styled(FormControl)`
  background: ${({ theme }) => theme.palette.background.paper};
  min-width: 280px;
`;
export default PaymentMethodsExceptionsPage;
